import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getMissions } from '../api'
import './Missions.css'

type MissionItem = {
  id: string
  drone_id: string
  name: string
  type: string
  action_type?: string
  location_lat?: string
  location_lng?: string
  status: string
  created_at?: string
  waypoints?: unknown[]
}

const ACTION_LABEL: Record<string, string> = {
  land: '🛬 נחיתה', takeoff: '🛫 המראה',
  drop_delivery: '📦 הורדת משלוח', scan: '🔍 סריקה', spray: '💧 ריסוס',
}
const TYPE_LABEL: Record<string, string> = {
  patrol: 'סיור', delivery: 'משלוח', mapping: 'מיפוי',
  inspection: 'בדיקה', survey: 'סקר', zone: 'אזור כיסוי', custom: 'מותאם',
}
const STATUS_LABEL: Record<string, string> = {
  draft: 'טיוטה', active: 'פעיל', completed: 'הושלם', cancelled: 'בוטל',
}

export default function Missions() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data: missions = [], isLoading, error } = useQuery<MissionItem[]>({
    queryKey: ['missions'],
    queryFn: getMissions,
  })

  if (isLoading) return <div className="page-loading">טוען משימות...</div>
  if (error) return <div className="page-error">שגיאה בטעינת משימות</div>

  const filtered = statusFilter === 'all'
    ? missions
    : missions.filter(m => m.status === statusFilter)

  return (
    <div className="missions-page">
      <div className="missions-header">
        <h1>{t('missions.title')}</h1>
        <div className="missions-header-actions">
          <select
            className="missions-status-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="all">כל הסטטוסים</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <button type="button" className="btn-primary" onClick={() => navigate('/missions/new')}>
            🗺️ + משימה חדשה
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="missions-empty-state">
          <div className="missions-empty-icon">🗺️</div>
          <p>{missions.length === 0 ? 'אין משימות. צור משימה חדשה עם מפת GPS.' : 'לא נמצאו משימות עבור הסינון הנבחר.'}</p>
          {missions.length === 0 && (
            <button className="btn-primary" onClick={() => navigate('/missions/new')}>
              צור משימה ראשונה
            </button>
          )}
        </div>
      ) : (
        <div className="missions-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>שם</th>
                <th>סוג</th>
                <th>פעולה</th>
                <th>מסלול</th>
                <th>מיקום יעד</th>
                <th>סטטוס</th>
                <th>תאריך</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const wpCount = Array.isArray(m.waypoints) ? m.waypoints.length : 0
                return (
                  <tr
                    key={m.id}
                    className="missions-row"
                    onClick={() => navigate(`/missions/${m.id}`)}
                  >
                    <td className="missions-name">{m.name}</td>
                    <td>{TYPE_LABEL[m.type] ?? m.type}</td>
                    <td>
                      {m.action_type
                        ? <span className="missions-action">{ACTION_LABEL[m.action_type] ?? m.action_type}</span>
                        : <span className="missions-no-action">—</span>
                      }
                    </td>
                    <td>
                      {m.type === 'zone' && wpCount >= 3
                        ? <span className="missions-zone-badge">⬡ {wpCount} נק׳</span>
                        : wpCount >= 2
                        ? <span className="missions-wp-badge">🗺 {wpCount} WP</span>
                        : <span className="missions-no-action">—</span>
                      }
                    </td>
                    <td>
                      {m.location_lat && m.location_lng
                        ? <code className="missions-coords">{parseFloat(m.location_lat).toFixed(4)}, {parseFloat(m.location_lng).toFixed(4)}</code>
                        : '—'
                      }
                    </td>
                    <td>
                      <span className={`status status-${m.status}`}>
                        {STATUS_LABEL[m.status] ?? m.status}
                      </span>
                    </td>
                    <td>{m.created_at ? new Date(m.created_at).toLocaleDateString('he-IL') : '—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="missions-view-btn" onClick={() => navigate(`/missions/${m.id}`)}>
                        צפייה / עריכה
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
